import { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import api from '../../api';
import { Layout } from '../../components/layout/Layout';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '../../components/ui/tooltip';
import { 
  Globe, ArrowLeft, Edit, Trash2, Wifi, Layers, Network, MapPin,
  CheckCircle, AlertTriangle, Monitor, Server, Printer, Router,
  Shield, HardDrive, Plus, Grid3X3, List, RefreshCw
} from 'lucide-react';
import { toast } from 'sonner';
import { usePermissions } from '../../hooks/usePermissions';
import { cn } from '../../lib/utils';

const statusOptions = ['Disponible', 'Usado', 'Reservado', 'DHCP'];
const deviceTypeOptions = ['Server', 'Switch', 'Firewall', 'Printer', 'Workstation', 'Router', 'AP', 'Otro'];

// Icon mapping for device types
const deviceIcons = {
  'Server': Server,
  'Switch': Network,
  'Firewall': Shield,
  'Printer': Printer,
  'Workstation': Monitor,
  'Router': Router,
  'AP': Wifi,
  'Otro': HardDrive,
  'Gateway': Globe,
  'default': Wifi
};

// Color mapping for status
const statusColors = {
  'Disponible': 'border-green-500/30 bg-green-500/10 text-green-700 hover:bg-green-500/20 dark:text-green-300',
  'Usado': 'border-blue-500/30 bg-blue-500/10 text-blue-700 hover:bg-blue-500/20 dark:text-blue-300',
  'Reservado': 'border-yellow-500/40 bg-yellow-500/10 text-yellow-700 hover:bg-yellow-500/20 dark:text-yellow-300',
  'DHCP': 'border-purple-500/30 bg-purple-500/10 text-purple-700 hover:bg-purple-500/20 dark:text-purple-300',
  'Gateway': 'border-cyan-500/50 bg-cyan-500/[.15] text-cyan-700 hover:bg-cyan-500/25 dark:text-cyan-300',
  'empty': 'border-border bg-muted/20 text-muted-foreground hover:bg-muted/60'
};

// Convertir IP <-> entero de 32 bits (para soportar cualquier máscara, no solo /24)
const ipToInt = (ip) => ip.split('.').reduce((acc, o) => ((acc << 8) + (Number(o) & 255)) >>> 0, 0) >>> 0;
const intToIp = (n) => [(n >>> 24) & 255, (n >>> 16) & 255, (n >>> 8) & 255, n & 255].join('.');
// Tope de celdas a dibujar (evita renderizar subnets enormes como /16)
const GRID_CAP = 4096; // hasta /20

const SubnetDetailVisual = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { canCreate, canUpdate, canDelete } = usePermissions('ipam.subnets');
  
  const [subnet, setSubnet] = useState(null);
  const [addresses, setAddresses] = useState([]);
  const [vlans, setVlans] = useState([]);
  const [sites, setSites] = useState([]);
  const [loading, setLoading] = useState(true);
  const [viewMode, setViewMode] = useState('grid'); // 'grid' or 'list'
  
  // Modal states
  const [editSubnetModal, setEditSubnetModal] = useState(false);
  const [deleteSubnetModal, setDeleteSubnetModal] = useState(false);
  const [ipModal, setIpModal] = useState(false);
  const [selectedIp, setSelectedIp] = useState(null);
  const [editingAddress, setEditingAddress] = useState(null);
  
  // Form data
  const [subnetForm, setSubnetForm] = useState({});
  const [ipForm, setIpForm] = useState({
    ip_address: '',
    hostname: '',
    mac_address: '',
    status: 'Disponible',
    device_type: '',
    notes: ''
  });
  const [saving, setSaving] = useState(false);
  const [consolidating, setConsolidating] = useState(false);

  // Consolidate IPs from assets
  const handleConsolidate = async () => {
    setConsolidating(true);
    try {
      const response = await api.post(`/ipam/subnets/${id}/consolidate`);
      const { consolidated, skipped, errors } = response.data;
      
      if (consolidated > 0) {
        toast.success(`Se consolidaron ${consolidated} IPs desde assets`);
        fetchAddresses();
        fetchSubnet();
      } else if (skipped > 0) {
        toast.info(`No hay nuevas IPs para consolidar (${skipped} ya existían)`);
      } else {
        toast.info('No se encontraron IPs de assets en esta subnet');
      }
      
      if (errors > 0) {
        console.warn('Errores en consolidación:', response.data.details.errors);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al consolidar');
    } finally {
      setConsolidating(false);
    }
  };

  // Calcula todas las IPs de la subnet con aritmética de 32 bits (soporta /23, /22, etc.)
  const calculateSubnetIps = () => {
    if (!subnet) return [];

    const mask = Number(subnet.mask);
    const totalAddresses = Math.pow(2, 32 - mask);
    const maskBits = mask === 0 ? 0 : (0xFFFFFFFF << (32 - mask)) >>> 0;
    const baseInt = (ipToInt(subnet.network) & maskBits) >>> 0;   // dirección de red normalizada
    const broadcastInt = (baseInt + totalAddresses - 1) >>> 0;
    const hasNetBcast = mask <= 30; // /31 y /32 no tienen red/broadcast

    const count = Math.min(totalAddresses, GRID_CAP);
    const ips = [];
    for (let i = 0; i < count; i++) {
      const ipInt = (baseInt + i) >>> 0;
      const ip = intToIp(ipInt);

      // Find if this IP is registered
      const registered = addresses.find(a => a.ip_address === ip);

      // Check if it's the gateway
      const isGateway = subnet.gateway === ip;
      const isNetwork = hasNetBcast && ipInt === baseInt;
      const isBroadcast = hasNetBcast && ipInt === broadcastInt;

      ips.push({
        ip,
        index: i,
        isGateway,
        isNetwork,
        isBroadcast,
        registered,
        status: isGateway ? 'Gateway' : (registered?.status || 'empty'),
        hostname: registered?.hostname || (isGateway ? 'Gateway' : ''),
        mac_address: registered?.mac_address || '',
        device_type: registered?.device_type || (isGateway ? 'Gateway' : ''),
        id: registered?.id,
        block: ip.split('.').slice(0, 3).join('.')  // bloque /24, ej "10.0.1"
      });
    }

    return ips;
  };

  // Agrupa las IPs por bloque /24 para el mapa visual
  const groupByBlock = (ips) => {
    const blocks = [];
    const byKey = {};
    ips.forEach((ipData) => {
      if (!byKey[ipData.block]) {
        byKey[ipData.block] = { block: ipData.block, ips: [] };
        blocks.push(byKey[ipData.block]);
      }
      byKey[ipData.block].ips.push(ipData);
    });
    return blocks;
  };

  const fetchSubnet = async () => {
    try {
      const response = await api.get(`/ipam/subnets/${id}`);
      setSubnet(response.data);
      setSubnetForm({
        vlan_id: response.data.vlan_id?.toString() || '',
        site_id: response.data.site_id?.toString() || '',
        network: response.data.network || '',
        mask: response.data.mask?.toString() || '',
        gateway: response.data.gateway || '',
        description: response.data.description || ''
      });
    } catch (error) {
      toast.error('Error al cargar Subnet');
      navigate('/ipam');
    }
  };

  const fetchAddresses = async () => {
    try {
      // Fetch addresses in batches since max limit is 100
      let allAddresses = [];
      let page = 1;
      let hasMore = true;
      
      while (hasMore && page <= 20) { // Max 2000 IPs (20 pages x 100) - cubre hasta /21
        const response = await api.get(`/ipam/addresses?subnet_id=${id}&limit=100&page=${page}`);
        const data = response.data.data || [];
        allAddresses = [...allAddresses, ...data];
        hasMore = data.length === 100;
        page++;
      }
      
      setAddresses(allAddresses);
    } catch (error) {
      console.error('Error loading addresses:', error);
    }
  };

  const fetchOptions = async () => {
    try {
      const [vlansRes, sitesRes] = await Promise.all([
        api.get('/ipam/vlans/all'),
        api.get('/config/sites?limit=100')
      ]);
      setVlans(vlansRes.data || []);
      setSites(sitesRes.data?.data || sitesRes.data || []);
    } catch (error) {
      console.error('Error loading options:', error);
    }
  };

  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      await Promise.all([fetchSubnet(), fetchAddresses(), fetchOptions()]);
      setLoading(false);
    };
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  // Handle IP cell click
  const handleIpClick = (ipData) => {
    if (ipData.isNetwork || ipData.isBroadcast) {
      toast.info(`${ipData.isNetwork ? 'Dirección de red' : 'Dirección de broadcast'} - No asignable`);
      return;
    }
    
    setSelectedIp(ipData.ip);
    setEditingAddress(ipData.registered ? ipData : null);
    setIpForm({
      ip_address: ipData.ip,
      hostname: ipData.hostname || '',
      mac_address: ipData.mac_address || '',
      status: ipData.registered?.status || 'Usado',
      device_type: ipData.device_type || '',
      notes: ipData.registered?.notes || ''
    });
    setIpModal(true);
  };

  // Save IP
  const handleSaveIp = async () => {
    setSaving(true);
    try {
      const payload = {
        subnet_id: parseInt(id),
        ip_address: ipForm.ip_address,
        hostname: ipForm.hostname || null,
        mac_address: ipForm.mac_address || null,
        status: ipForm.status,
        device_type: ipForm.device_type || null,
        notes: ipForm.notes || null
      };

      if (editingAddress?.id) {
        await api.put(`/ipam/addresses/${editingAddress.id}`, payload);
        toast.success('IP actualizada');
      } else {
        await api.post('/ipam/addresses', payload);
        toast.success('IP registrada');
      }

      setIpModal(false);
      fetchAddresses();
      fetchSubnet();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al guardar');
    } finally {
      setSaving(false);
    }
  };

  // Delete IP
  const handleDeleteIp = async () => {
    if (!editingAddress?.id) return;
    
    try {
      await api.delete(`/ipam/addresses/${editingAddress.id}`);
      toast.success('IP eliminada');
      setIpModal(false);
      fetchAddresses();
      fetchSubnet();
    } catch (error) {
      toast.error('Error al eliminar');
    }
  };

  // Save subnet
  const handleSaveSubnet = async () => {
    setSaving(true);
    try {
      await api.put(`/ipam/subnets/${id}`, {
        ...subnetForm,
        vlan_id: subnetForm.vlan_id ? parseInt(subnetForm.vlan_id) : null,
        site_id: subnetForm.site_id ? parseInt(subnetForm.site_id) : null,
        mask: parseInt(subnetForm.mask),
        gateway: subnetForm.gateway || null
      });
      toast.success('Subnet actualizada');
      setEditSubnetModal(false);
      fetchSubnet();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al guardar');
    } finally {
      setSaving(false);
    }
  };

  // Delete subnet
  const handleDeleteSubnet = async () => {
    try {
      await api.delete(`/ipam/subnets/${id}`);
      toast.success('Subnet eliminada');
      navigate('/ipam');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al eliminar');
    }
  };

  const getUtilizationColor = (util) => {
    if (util >= 90) return 'text-red-500';
    if (util >= 75) return 'text-yellow-500';
    return 'text-green-500';
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
        </div>
      </Layout>
    );
  }

  if (!subnet) return null;

  const subnetIps = calculateSubnetIps();
  const subnetBlocks = groupByBlock(subnetIps);
  const truncated = subnet && Math.pow(2, 32 - Number(subnet.mask)) > GRID_CAP;
  const occupiedIps = subnet.occupied_ips || 0;
  const utilization = subnet.utilization || 0;

  return (
    <Layout>
      <div className="space-y-6" data-testid="subnet-detail-visual">
        {/* Header */}
        <div className="flex flex-col items-start gap-4 xl:flex-row xl:items-center xl:justify-between">
          <div className="flex items-center gap-4">
            <Button variant="ghost" size="icon" onClick={() => navigate('/ipam')}>
              <ArrowLeft className="h-5 w-5" />
            </Button>
            <div className="flex items-center gap-3">
              <Globe className="h-8 w-8 text-primary" strokeWidth={1.5} />
              <div>
                <h1 className="text-2xl font-bold text-foreground font-mono">
                  {subnet.network}/{subnet.mask}
                </h1>
                <p className="text-sm text-muted-foreground">
                  {subnet.description || 'Subnet IP'} 
                  {subnet.vlan_number && ` • VLAN ${subnet.vlan_number}`}
                  {subnet.site_name && ` • ${subnet.site_name}`}
                </p>
              </div>
            </div>
          </div>
          <div className="flex flex-wrap gap-2">
            <Button variant="outline" size="sm" onClick={() => fetchAddresses()}>
              <RefreshCw className="h-4 w-4 mr-2" />
              Refrescar
            </Button>
            <Button 
              variant="outline" 
              size="sm" 
              onClick={handleConsolidate}
              disabled={consolidating}
              title="Importar IPs de Servers, Switches, Impresoras, etc. a esta subnet"
            >
              {consolidating ? (
                <>
                  <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                  Consolidando...
                </>
              ) : (
                <>
                  <Server className="h-4 w-4 mr-2" />
                  Consolidar IPs
                </>
              )}
            </Button>
            {canUpdate && (
              <Button variant="outline" onClick={() => setEditSubnetModal(true)}>
                <Edit className="h-4 w-4 mr-2" />
                Editar
              </Button>
            )}
            {canDelete && (
              <Button variant="destructive" onClick={() => setDeleteSubnetModal(true)}>
                <Trash2 className="h-4 w-4 mr-2" />
                Eliminar
              </Button>
            )}
          </div>
        </div>

        {/* Stats Row */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
          <Card className="stats-card">
            <CardContent className="p-4">
              <div className="text-2xl font-bold text-primary">{subnet.used_ips || 0}</div>
              <div className="text-xs text-muted-foreground">IPs Registradas</div>
            </CardContent>
          </Card>
          <Card className="stats-card">
            <CardContent className="p-4">
              <div className="text-2xl font-bold text-primary">{subnet.total_hosts || 0}</div>
              <div className="text-xs text-muted-foreground">Total Hosts</div>
            </CardContent>
          </Card>
          <Card className="stats-card">
            <CardContent className="p-4">
              <div className={`text-2xl font-bold ${getUtilizationColor(utilization)}`}>{utilization}%</div>
              <div className="text-xs text-muted-foreground">Utilización</div>
            </CardContent>
          </Card>
          <Card className="stats-card">
            <CardContent className="p-4">
              <div className="text-2xl font-bold text-green-500">{Math.max(0, (subnet.total_hosts || 0) - occupiedIps)}</div>
              <div className="text-xs text-muted-foreground">Disponibles</div>
            </CardContent>
          </Card>
          <Card className="stats-card">
            <CardContent className="p-4">
              <div className="text-2xl font-bold font-mono text-primary">{subnet.gateway || '-'}</div>
              <div className="text-xs text-muted-foreground">Gateway</div>
            </CardContent>
          </Card>
        </div>

        {/* Utilization Bar */}
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm font-medium">Utilización de la Subnet</span>
              <span className={cn("text-sm font-bold", getUtilizationColor(utilization))}>
                {occupiedIps} / {subnet.total_hosts || 0} ({utilization}%)
              </span>
            </div>
            <div className="w-full bg-muted rounded-full h-4 overflow-hidden">
              <div 
                className={cn(
                  "h-4 rounded-full transition-all",
                  utilization >= 90 ? "bg-red-500" :
                  utilization >= 75 ? "bg-yellow-500" :
                  utilization >= 50 ? "bg-blue-500" : "bg-green-500"
                )}
                style={{ width: `${Math.min(utilization, 100)}%` }}
              />
            </div>
            <p className="text-xs text-muted-foreground mt-2">Incluye direcciones usadas, reservadas y DHCP.</p>
          </CardContent>
        </Card>

        {/* IP Grid / List */}
        <Card>
          <CardHeader className="flex flex-row items-center justify-between pb-3">
            <CardTitle className="text-sm font-medium flex items-center gap-2">
              <Grid3X3 className="h-4 w-4 text-primary" />
              Mapa de Direcciones IP
            </CardTitle>
            <div className="flex items-center gap-2">
              <Button
                variant={viewMode === 'grid' ? 'default' : 'outline'}
                size="sm"
                aria-label="Vista de mapa"
                aria-pressed={viewMode === 'grid'}
                onClick={() => setViewMode('grid')}
              >
                <Grid3X3 className="h-4 w-4" />
              </Button>
              <Button
                variant={viewMode === 'list' ? 'default' : 'outline'}
                size="sm"
                aria-label="Vista de lista"
                aria-pressed={viewMode === 'list'}
                onClick={() => setViewMode('list')}
              >
                <List className="h-4 w-4" />
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            {/* Legend */}
            <div className="flex flex-wrap items-center gap-4 mb-4 pb-4 border-b text-xs">
              <div className="flex items-center gap-1.5">
                <div className="w-3 h-3 rounded-sm border border-cyan-500/50 bg-cyan-500/[.15]"></div>
                <span>Gateway</span>
              </div>
              <div className="flex items-center gap-1.5">
                <div className="w-3 h-3 rounded-sm border border-blue-500/30 bg-blue-500/10"></div>
                <span>Usado</span>
              </div>
              <div className="flex items-center gap-1.5">
                <div className="w-3 h-3 rounded-sm border border-green-500/30 bg-green-500/10"></div>
                <span>Disponible</span>
              </div>
              <div className="flex items-center gap-1.5">
                <div className="w-3 h-3 rounded-sm border border-yellow-500/40 bg-yellow-500/10"></div>
                <span>Reservado</span>
              </div>
              <div className="flex items-center gap-1.5">
                <div className="w-3 h-3 rounded-sm border border-purple-500/30 bg-purple-500/10"></div>
                <span>DHCP</span>
              </div>
              <div className="flex items-center gap-1.5">
                <div className="w-3 h-3 rounded-sm border border-border bg-muted/20"></div>
                <span>Libre</span>
              </div>
              <div className="flex items-center gap-1.5">
                <div className="w-3 h-3 rounded-sm border border-dashed border-muted-foreground/50 bg-muted/50"></div>
                <span>Red / Broadcast</span>
              </div>
            </div>

            {viewMode === 'grid' ? (
              /* Grid View - agrupado por bloque /24 */
              <TooltipProvider delayDuration={200}>
                <div className="space-y-4">
                  {truncated && (
                    <div className="text-xs text-yellow-600 flex items-center gap-1.5">
                      <AlertTriangle className="h-3.5 w-3.5" />
                      Subnet muy grande: se muestran las primeras {GRID_CAP} direcciones.
                    </div>
                  )}
                  {subnetBlocks.map((blk) => (
                    <div key={blk.block} className="max-w-[960px]">
                      <div className="flex items-center gap-2 mb-2 text-xs text-muted-foreground">
                        <span className="font-mono font-semibold text-foreground">{blk.block}.x</span>
                        <span>·</span>
                        <span>{blk.ips.length} direcciones</span>
                      </div>
                      <div className="grid grid-cols-4 sm:grid-cols-8 lg:grid-cols-[repeat(16,minmax(0,1fr))] gap-1.5">
                        {blk.ips.map((ipData) => {
                          const IconComponent = deviceIcons[ipData.device_type] || deviceIcons.default;
                          const reservedType = ipData.isNetwork ? 'Red' : ipData.isBroadcast ? 'Broadcast' : null;
                          const status = reservedType || (ipData.status === 'empty' ? 'Libre' : ipData.status);

                          return (
                            <Tooltip key={ipData.ip}>
                              <TooltipTrigger asChild>
                                <button
                                  onClick={() => handleIpClick(ipData)}
                                  aria-disabled={Boolean(reservedType)}
                                  aria-label={`${ipData.ip}, ${status}${ipData.hostname ? `, ${ipData.hostname}` : ''}`}
                                  className={cn(
                                    "h-11 min-w-0 rounded-md border flex items-center justify-center gap-1 px-1 font-mono text-xs font-medium transition-colors",
                                    reservedType
                                      ? "flex-col gap-0 border-dashed border-muted-foreground/40 bg-muted/50 text-muted-foreground cursor-not-allowed"
                                      : statusColors[ipData.status] || statusColors.empty,
                                    "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-1"
                                  )}
                                >
                                  <span>.{ipData.ip.split('.')[3]}</span>
                                  {reservedType ? (
                                    <span className="font-sans text-[10px] uppercase leading-none">{ipData.isNetwork ? 'Red' : 'Bcast'}</span>
                                  ) : (ipData.device_type || ipData.isGateway) && (
                                    <IconComponent className="h-3.5 w-3.5 shrink-0" strokeWidth={1.5} aria-hidden="true" />
                                  )}
                                </button>
                              </TooltipTrigger>
                              <TooltipContent side="top" className="max-w-xs">
                                <div className="text-xs space-y-1">
                                  <div className="font-mono font-bold">{ipData.ip}</div>
                                  {ipData.hostname && <div>Host: {ipData.hostname}</div>}
                                  {ipData.mac_address && <div>MAC: {ipData.mac_address}</div>}
                                  {!reservedType && <div>Estado: {status}</div>}
                                  {ipData.device_type && <div>Tipo: {ipData.device_type}</div>}
                                  {ipData.isNetwork && <div className="text-yellow-500">Dirección de Red</div>}
                                  {ipData.isBroadcast && <div className="text-yellow-500">Broadcast</div>}
                                </div>
                              </TooltipContent>
                            </Tooltip>
                          );
                        })}
                      </div>
                    </div>
                  ))}
                </div>
              </TooltipProvider>
            ) : (
              /* List View */
              <div className="overflow-x-auto max-h-[500px]">
                <table className="w-full table-subtle text-sm">
                  <thead className="sticky top-0 bg-background">
                    <tr className="border-b">
                      <th className="text-left p-2 text-xs uppercase">IP</th>
                      <th className="text-left p-2 text-xs uppercase">Hostname</th>
                      <th className="text-left p-2 text-xs uppercase">MAC</th>
                      <th className="text-center p-2 text-xs uppercase">Estado</th>
                      <th className="text-left p-2 text-xs uppercase">Tipo</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y">
                    {subnetIps.filter(ip => ip.registered || ip.isGateway).map((ipData) => (
                      <tr 
                        key={ipData.ip} 
                        className="hover:bg-muted/30 cursor-pointer"
                        onClick={() => handleIpClick(ipData)}
                      >
                        <td className="p-2 font-mono text-primary font-medium">{ipData.ip}</td>
                        <td className="p-2">{ipData.hostname || '-'}</td>
                        <td className="p-2 font-mono text-xs">{ipData.mac_address || '-'}</td>
                        <td className="p-2 text-center">
                          <span className={cn(
                            "inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium",
                            ipData.status === 'Gateway' && "bg-cyan-500/20 text-cyan-600",
                            ipData.status === 'Usado' && "bg-blue-500/20 text-blue-600",
                            ipData.status === 'Disponible' && "bg-green-500/20 text-green-600",
                            ipData.status === 'Reservado' && "bg-yellow-500/20 text-yellow-600",
                            ipData.status === 'DHCP' && "bg-purple-500/20 text-purple-600"
                          )}>
                            {ipData.status}
                          </span>
                        </td>
                        <td className="p-2 text-muted-foreground">{ipData.device_type || '-'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                {addresses.length === 0 && !subnet.gateway && (
                  <div className="text-center py-8 text-muted-foreground">
                    No hay direcciones IP registradas
                  </div>
                )}
              </div>
            )}
          </CardContent>
        </Card>

        {/* IP Edit/Create Modal */}
        <Dialog open={ipModal} onOpenChange={setIpModal}>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Wifi className="h-5 w-5 text-primary" />
                {editingAddress?.id ? 'Editar' : 'Registrar'} IP: {selectedIp}
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div>
                <Label>Dirección IP</Label>
                <Input value={ipForm.ip_address} disabled className="mt-1.5 font-mono bg-muted" />
              </div>
              <div>
                <Label>Hostname</Label>
                <Input
                  value={ipForm.hostname}
                  onChange={(e) => setIpForm(prev => ({ ...prev, hostname: e.target.value }))}
                  placeholder="server01.domain.local"
                  className="mt-1.5"
                />
              </div>
              <div>
                <Label>MAC Address</Label>
                <Input
                  value={ipForm.mac_address}
                  onChange={(e) => setIpForm(prev => ({ ...prev, mac_address: e.target.value }))}
                  placeholder="AA:BB:CC:DD:EE:FF"
                  className="mt-1.5 font-mono"
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Estado</Label>
                  <Select value={ipForm.status} onValueChange={(v) => setIpForm(prev => ({ ...prev, status: v }))}>
                    <SelectTrigger className="mt-1.5"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {statusOptions.map((s) => (<SelectItem key={s} value={s}>{s}</SelectItem>))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Tipo Dispositivo</Label>
                  <Select value={ipForm.device_type || "none"} onValueChange={(v) => setIpForm(prev => ({ ...prev, device_type: v === "none" ? "" : v }))}>
                    <SelectTrigger className="mt-1.5"><SelectValue placeholder="Seleccionar" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">Sin tipo</SelectItem>
                      {deviceTypeOptions.map((t) => (<SelectItem key={t} value={t}>{t}</SelectItem>))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div>
                <Label>Notas</Label>
                <Input
                  value={ipForm.notes}
                  onChange={(e) => setIpForm(prev => ({ ...prev, notes: e.target.value }))}
                  placeholder="Notas adicionales"
                  className="mt-1.5"
                />
              </div>
            </div>
            <DialogFooter className="flex justify-between">
              <div>
                {editingAddress?.id && canDelete && (
                  <Button variant="destructive" onClick={handleDeleteIp}>
                    <Trash2 className="h-4 w-4 mr-2" />
                    Eliminar
                  </Button>
                )}
              </div>
              <div className="flex gap-2">
                <Button variant="outline" onClick={() => setIpModal(false)}>Cancelar</Button>
                <Button onClick={handleSaveIp} disabled={saving}>
                  {saving ? 'Guardando...' : 'Guardar'}
                </Button>
              </div>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Edit Subnet Modal */}
        <Dialog open={editSubnetModal} onOpenChange={setEditSubnetModal}>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Globe className="h-5 w-5 text-primary" />
                Editar Subnet
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Red</Label>
                  <Input value={subnetForm.network} onChange={(e) => setSubnetForm(prev => ({ ...prev, network: e.target.value }))} className="mt-1.5 font-mono" />
                </div>
                <div>
                  <Label>Máscara</Label>
                  <Input type="number" value={subnetForm.mask} onChange={(e) => setSubnetForm(prev => ({ ...prev, mask: e.target.value }))} className="mt-1.5" />
                </div>
              </div>
              <div>
                <Label>Gateway</Label>
                <Input value={subnetForm.gateway} onChange={(e) => setSubnetForm(prev => ({ ...prev, gateway: e.target.value }))} className="mt-1.5 font-mono" />
              </div>
              <div>
                <Label>VLAN</Label>
                <Select value={subnetForm.vlan_id || "none"} onValueChange={(v) => setSubnetForm(prev => ({ ...prev, vlan_id: v === "none" ? "" : v }))}>
                  <SelectTrigger className="mt-1.5"><SelectValue placeholder="Sin VLAN" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">Sin VLAN</SelectItem>
                    {vlans.map((v) => (<SelectItem key={v.id} value={String(v.id)}>{v.vlan_number} - {v.name}</SelectItem>))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Site</Label>
                <Select value={subnetForm.site_id || "none"} onValueChange={(v) => setSubnetForm(prev => ({ ...prev, site_id: v === "none" ? "" : v }))}>
                  <SelectTrigger className="mt-1.5"><SelectValue placeholder="Sin Site" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">Sin Site</SelectItem>
                    {sites.map((s) => (<SelectItem key={s.id} value={String(s.id)}>{s.site}</SelectItem>))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Descripción</Label>
                <Input value={subnetForm.description} onChange={(e) => setSubnetForm(prev => ({ ...prev, description: e.target.value }))} className="mt-1.5" />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setEditSubnetModal(false)}>Cancelar</Button>
              <Button onClick={handleSaveSubnet} disabled={saving}>{saving ? 'Guardando...' : 'Guardar'}</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Delete Subnet Modal */}
        <Dialog open={deleteSubnetModal} onOpenChange={setDeleteSubnetModal}>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2 text-destructive">
                <Trash2 className="h-5 w-5" />
                Confirmar Eliminación
              </DialogTitle>
            </DialogHeader>
            <p className="text-muted-foreground py-4">
              ¿Eliminar Subnet <strong>{subnet.network}/{subnet.mask}</strong>?
              {addresses.length > 0 && (
                <span className="block mt-2 text-yellow-600">
                  Esta subnet tiene {addresses.length} direcciones IP registradas.
                </span>
              )}
            </p>
            <DialogFooter>
              <Button variant="outline" onClick={() => setDeleteSubnetModal(false)}>Cancelar</Button>
              <Button variant="destructive" onClick={handleDeleteSubnet}>Eliminar</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
};

export default SubnetDetailVisual;
